import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'

export interface ScannedData {
  barcode: string
  type: string
  timestamp: string
  source: 'scanner' | 'file'
  rawData?: any
}

export interface MappedData {
  productIdentification: {
    gtin: string
    oem_number: string
    product_name: string
    manufacturer: string
  }
  materials: {
    composition: Array<{ material: string; weight_percent: number }>
    recyclingContent: number
    hazardousSubstances: string
  }
  sustainability: {
    co2Footprint: number
    energyClass: string
    waterConsumption?: number
    certifications: string[]
  }
  supplyChain: {
    originCountry: string
    supplier: string
    transportConditions: Record<string, any>
    supplierCertifications: string[]
  }
  compliance: {
    ceMarked: boolean
    reachCompliant: boolean
    rohsCompliant: boolean
    localRegulations: string[]
  }
  missingFields: string[]
}

interface ScannerState {
  isScanning: boolean
  scannedData: ScannedData | null
  mappedData: MappedData | null
  processing: boolean
  progress: number
  error: string | null
  scanHistory: ScannedData[]
  supportedFormats: string[]
}

const initialState: ScannerState = {
  isScanning: false,
  scannedData: null,
  mappedData: null,
  processing: false,
  progress: 0,
  error: null,
  scanHistory: [],
  supportedFormats: ['EAN13', 'EAN8', 'UPC', 'Code128', 'Code39', 'QR Code'],
}

// Async thunks
export const processBarcode = createAsyncThunk(
  'scanner/processBarcode',
  async (barcode: string) => {
    const response = await fetch('/api/v1/dpp/scan', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        barcode,
        barcode_type: detectBarcodeType(barcode),
        scanner_id: 'web-scanner',
        timestamp: new Date().toISOString(),
      }),
    })
    
    if (!response.ok) {
      throw new Error('Fehler bei der Barcode-Verarbeitung')
    }
    
    return response.json()
  }
)

export const processFile = createAsyncThunk(
  'scanner/processFile',
  async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await fetch('/api/v1/import/file', {
      method: 'POST',
      body: formData,
    })
    
    if (!response.ok) {
      throw new Error('Fehler bei der Datei-Verarbeitung')
    }
    
    return response.json()
  }
)

export const enhanceWithLLM = createAsyncThunk(
  'scanner/enhanceWithLLM',
  async (data: { data: any; type: string }) => {
    const response = await fetch('/api/v1/llm/enhance', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    
    if (!response.ok) {
      throw new Error('Fehler bei der LLM-Verbesserung')
    }
    
    return response.json()
  }
)

export const mapDataWithLLM = createAsyncThunk(
  'scanner/mapDataWithLLM',
  async (data: { source_data: any; source_format: string }) => {
    const response = await fetch('/api/v1/llm/map-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    
    if (!response.ok) {
      throw new Error('Fehler beim LLM-Mapping')
    }
    
    return response.json()
  }
)

// Helper function
function detectBarcodeType(barcode: string): string {
  if (barcode.length === 13) return 'EAN13'
  if (barcode.length === 8) return 'EAN8'
  if (barcode.length === 12) return 'UPC'
  if (barcode.match(/^[A-Z0-9]{17}$/)) return 'VIN'
  return 'CODE128'
}

const scannerSlice = createSlice({
  name: 'scanner',
  initialState,
  reducers: {
    startScanning: (state) => {
      state.isScanning = true
      state.error = null
    },
    stopScanning: (state) => {
      state.isScanning = false
    },
    setScannedData: (state, action: PayloadAction<ScannedData>) => {
      state.scannedData = action.payload
      state.scanHistory.push(action.payload)
    },
    setMappedData: (state, action: PayloadAction<MappedData>) => {
      state.mappedData = action.payload
    },
    setProgress: (state, action: PayloadAction<number>) => {
      state.progress = action.payload
    },
    clearScannedData: (state) => {
      state.scannedData = null
      state.mappedData = null
      state.error = null
      state.progress = 0
    },
    clearError: (state) => {
      state.error = null
    },
    addToHistory: (state, action: PayloadAction<ScannedData>) => {
      state.scanHistory.unshift(action.payload)
      // Keep only last 100 scans
      if (state.scanHistory.length > 100) {
        state.scanHistory = state.scanHistory.slice(0, 100)
      }
    },
  },
  extraReducers: (builder) => {
    // Process Barcode
    builder
      .addCase(processBarcode.pending, (state) => {
        state.processing = true
        state.error = null
        state.progress = 0
      })
      .addCase(processBarcode.fulfilled, (state, action) => {
        state.processing = false
        state.progress = 100
        state.mappedData = action.payload
      })
      .addCase(processBarcode.rejected, (state, action) => {
        state.processing = false
        state.error = action.error.message || 'Fehler bei der Barcode-Verarbeitung'
      })

    // Process File
    builder
      .addCase(processFile.pending, (state) => {
        state.processing = true
        state.error = null
        state.progress = 0
      })
      .addCase(processFile.fulfilled, (state, action) => {
        state.processing = false
        state.progress = 100
        if (action.payload.scannedData) {
          state.scannedData = action.payload.scannedData
          state.scanHistory.push(action.payload.scannedData)
        }
        if (action.payload.mappedData) {
          state.mappedData = action.payload.mappedData
        }
      })
      .addCase(processFile.rejected, (state, action) => {
        state.processing = false
        state.error = action.error.message || 'Fehler bei der Datei-Verarbeitung'
      })

    // Enhance with LLM
    builder
      .addCase(enhanceWithLLM.pending, (state) => {
        state.processing = true
        state.error = null
        state.progress = 0
      })
      .addCase(enhanceWithLLM.fulfilled, (state, action) => {
        state.processing = false
        state.progress = 100
        if (state.mappedData) {
          state.mappedData = { ...state.mappedData, ...action.payload }
        }
      })
      .addCase(enhanceWithLLM.rejected, (state, action) => {
        state.processing = false
        state.error = action.error.message || 'Fehler bei der LLM-Verbesserung'
      })

    // Map Data with LLM
    builder
      .addCase(mapDataWithLLM.pending, (state) => {
        state.processing = true
        state.error = null
        state.progress = 0
      })
      .addCase(mapDataWithLLM.fulfilled, (state, action) => {
        state.processing = false
        state.progress = 100
        state.mappedData = action.payload
      })
      .addCase(mapDataWithLLM.rejected, (state, action) => {
        state.processing = false
        state.error = action.error.message || 'Fehler beim LLM-Mapping'
      })
  },
})

export const {
  startScanning,
  stopScanning,
  setScannedData,
  setMappedData,
  setProgress,
  clearScannedData,
  clearError,
  addToHistory,
} = scannerSlice.actions

export default scannerSlice.reducer
